import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { DoorOpen, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listResources, type Resource } from '../../lib/resources-api'

const kindVariant: Record<string, 'success' | 'secondary' | 'gold' | 'neutral'> = {
  room: 'secondary',
  equipment: 'gold',
  vehicle: 'success',
  other: 'neutral',
}

function ResourceRow({ resource }: { resource: Resource }) {
  return (
    <tr className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover">
      <td className="px-5 py-3">
        <Link
          to={`/app/resources/${resource.id}`}
          className="flex items-center gap-3 font-medium text-fg hover:text-secondary"
        >
          <span
            className="grid size-8 shrink-0 place-items-center rounded-md border border-border bg-surface-2"
            style={resource.color ? { color: resource.color } : undefined}
            aria-hidden="true"
          >
            <DoorOpen className="size-4" />
          </span>
          <span className="truncate">{resource.name}</span>
        </Link>
      </td>
      <td className="px-5 py-3">
        <Badge variant={kindVariant[resource.kind] ?? 'neutral'} className="capitalize">
          {resource.kind}
        </Badge>
      </td>
      <td className="px-5 py-3 text-muted">{resource.siteName ?? '—'}</td>
      <td className="px-5 py-3 text-muted">{resource.location ?? '—'}</td>
      <td className="px-5 py-3 text-muted">{resource.capacity ?? '—'}</td>
      <td className="px-5 py-3">
        {resource.isBookable ? (
          <Badge variant="success">Bookable</Badge>
        ) : (
          <Badge variant="neutral">Unavailable</Badge>
        )}
      </td>
    </tr>
  )
}

export function ResourcesList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['resources'],
    queryFn: () => listResources(),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Resources"
        description="Bookable rooms, equipment, and vehicles for your ministries."
        actions={
          <Button asChild>
            <Link to="/app/resources/new">
              <Plus />
              Add resource
            </Link>
          </Button>
        }
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading resources…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load resources.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="space-y-3 py-20 text-center">
            <p className="text-sm text-muted">No resources yet.</p>
            <Button asChild variant="secondary" size="sm">
              <Link to="/app/resources/new">
                <Plus />
                Add your first resource
              </Link>
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Kind</th>
                  <th className="px-5 py-3 font-semibold">Site</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Capacity</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <ResourceRow key={r.id} resource={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
